import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Users, UserPlus, Loader2, Crown, Shield, Mail, X, Clock, Send,
} from 'lucide-react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const initials = (name, email) => {
  const base = (name || email || '?').trim();
  const parts = base.split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || base[0]?.toUpperCase();
};

export const TeamManager = () => {
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [currentUserId, setCurrentUserId] = useState('');
  const [email, setEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // member object

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/team');
      setMembers(data.members || []);
      setInvites(data.invites || []);
      setCurrentUserId(data.current_user_id || '');
    } catch (e) {
      toast.error('Could not load your team');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    const addr = email.trim();
    if (!addr) return;
    setInviting(true);
    try {
      const { data } = await api.post('/team/invite', { email: addr });
      setEmail('');
      if (data.email_sent) toast.success(`Invitation sent to ${data.email}`);
      else toast.warning(`Invite created for ${data.email}, but the email couldn't be sent. Check your email settings.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send invitation');
    } finally { setInviting(false); }
  };

  const cancelInvite = async (id) => {
    try {
      await api.delete(`/team/invite/${id}`);
      setInvites((prev) => prev.filter((i) => i.id !== id));
      toast.success('Invitation cancelled');
    } catch (e) { toast.error('Could not cancel invitation'); }
  };

  const removeMember = async (m) => {
    try {
      await api.delete(`/team/member/${m.id}`);
      setMembers((prev) => prev.filter((x) => x.id !== m.id));
      toast.success(`${m.name || m.email} no longer has access`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove member');
    } finally { setConfirmRemove(null); }
  };

  return (
    <Card data-testid="team-manager">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="h-[18px] w-[18px] text-primary" /> Team &amp; admins
        </CardTitle>
        <CardDescription>
          Invite people to help manage this workspace. Admins share the same events, contacts, landing page and settings, with full access.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Invite form */}
        <form onSubmit={invite} className="flex flex-col sm:flex-row gap-2" data-testid="team-invite-form">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              className="pl-9 h-11"
              data-testid="team-invite-email"
            />
          </div>
          <Button type="submit" disabled={inviting || !email.trim()} className="gap-2 h-11" data-testid="team-invite-button">
            {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Send invite
          </Button>
        </form>

        {loading ? (
          <div className="space-y-2">{[1, 2].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
        ) : (
          <div className="space-y-2" data-testid="team-members">
            {members.map((m) => {
              const isSelf = m.id === currentUserId;
              const canRemove = !m.is_owner && !isSelf;
              return (
                <div key={m.id} className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5" data-testid="team-member-row">
                  <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {initials(m.name, m.email)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{m.name || m.email}</span>
                      {isSelf && <span className="text-xs text-muted-foreground">(you)</span>}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">{m.email}</div>
                  </div>
                  {m.is_owner ? (
                    <Badge variant="secondary" className="gap-1 text-primary"><Crown className="h-3 w-3" /> Owner</Badge>
                  ) : (
                    <Badge variant="secondary" className="gap-1"><Shield className="h-3 w-3" /> Admin</Badge>
                  )}
                  {canRemove && (
                    <Button
                      size="icon" variant="ghost"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => setConfirmRemove(m)}
                      aria-label={`Remove ${m.name || m.email}`}
                      data-testid="team-remove-member"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pending invitations */}
        {invites.length > 0 && (
          <div className="space-y-2" data-testid="team-pending-invites">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pending invitations</p>
            {invites.map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-secondary/30 px-3 py-2.5" data-testid="team-invite-row">
                <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{inv.email}</div>
                  <div className="text-sm text-muted-foreground truncate">
                    Invited{inv.invited_by_name ? ` by ${inv.invited_by_name}` : ''} · awaiting acceptance
                  </div>
                </div>
                <Badge variant="secondary" className="gap-1 text-muted-foreground"><Send className="h-3 w-3" /> Sent</Badge>
                <Button
                  size="icon" variant="ghost"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => cancelInvite(inv.id)}
                  aria-label={`Cancel invitation to ${inv.email}`}
                  data-testid="team-cancel-invite"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!confirmRemove} onOpenChange={(o) => !o && setConfirmRemove(null)}>
        <AlertDialogContent data-testid="team-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmRemove?.name || confirmRemove?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              They&apos;ll immediately lose access to this workspace and can no longer sign in. Your events, contacts and data stay intact. You can always invite them again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="team-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => removeMember(confirmRemove)} className="gap-1.5 bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="team-remove-confirm">
              <X className="h-4 w-4" /> Remove access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
